import { ITEMS, RARITY_COLORS } from 'shared';
import type { InventoryItem, ItemDefinition } from 'shared';

export interface InventoryConfig {
  slots: number;
}

export class InventorySystem {
  private items: (InventoryItem | null)[];
  private maxSlots: number;
  private gold: number = 0;

  // UI elements
  private container: HTMLDivElement;
  private isOpen: boolean = false;

  // Event callbacks
  public onItemPickup?: (item: ItemDefinition, quantity: number) => void;
  public onGoldChange?: (gold: number) => void;

  constructor(config: InventoryConfig) {
    this.maxSlots = config.slots;
    this.items = new Array(config.slots).fill(null);

    // Create inventory UI
    this.container = this.createInventoryUI();
    document.getElementById('game-container')?.appendChild(this.container);

    // Keyboard toggle
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyI' || e.code === 'KeyB') {
        this.toggle();
      }
    });
  }

  private createInventoryUI(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'inventory-panel';
    container.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: linear-gradient(180deg, rgba(30,30,50,0.95) 0%, rgba(20,20,35,0.98) 100%);
      border: 2px solid #4a4a6a;
      border-radius: 8px;
      padding: 20px;
      display: none;
      z-index: 200;
      min-width: 320px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.5);
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 1px solid #4a4a6a;
    `;

    const title = document.createElement('h2');
    title.textContent = 'Inventory';
    title.style.cssText = `
      color: #e0e0e0;
      margin: 0;
      font-size: 18px;
    `;
    header.appendChild(title);

    const goldDisplay = document.createElement('div');
    goldDisplay.id = 'inventory-gold';
    goldDisplay.style.cssText = `
      color: #ffd700;
      font-size: 16px;
    `;
    goldDisplay.textContent = '💰 0';
    header.appendChild(goldDisplay);

    container.appendChild(header);

    // Slots grid
    const grid = document.createElement('div');
    grid.id = 'inventory-grid';
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(5, 50px);
      gap: 5px;
    `;

    for (let i = 0; i < this.maxSlots; i++) {
      const slot = document.createElement('div');
      slot.className = 'inventory-slot';
      slot.dataset.slot = i.toString();
      slot.style.cssText = `
        width: 50px;
        height: 50px;
        background: rgba(0,0,0,0.4);
        border: 1px solid #3a3a5a;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        position: relative;
        cursor: pointer;
        transition: border-color 0.2s;
      `;

      slot.addEventListener('mouseenter', () => {
        slot.style.borderColor = '#6a6a9a';
      });
      slot.addEventListener('mouseleave', () => {
        slot.style.borderColor = '#3a3a5a';
      });

      // Tooltip on hover
      slot.addEventListener('mouseenter', (e) => this.showTooltip(e, i));
      slot.addEventListener('mouseleave', () => this.hideTooltip());

      grid.appendChild(slot);
    }

    container.appendChild(grid);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: none;
      border: none;
      color: #888;
      font-size: 20px;
      cursor: pointer;
    `;
    closeBtn.addEventListener('click', () => this.close());
    container.appendChild(closeBtn);

    // Tooltip element
    const tooltip = document.createElement('div');
    tooltip.id = 'inventory-tooltip';
    tooltip.style.cssText = `
      position: absolute;
      background: rgba(20,20,30,0.95);
      border: 1px solid #4a4a6a;
      border-radius: 4px;
      padding: 10px;
      color: #e0e0e0;
      font-size: 12px;
      pointer-events: none;
      display: none;
      z-index: 300;
      max-width: 200px;
    `;
    document.body.appendChild(tooltip);

    return container;
  }

  private showTooltip(event: MouseEvent, slotIndex: number): void {
    const item = this.items[slotIndex];
    if (!item) return;

    const itemDef = ITEMS[item.itemId];
    if (!itemDef) return;

    const tooltip = document.getElementById('inventory-tooltip');
    if (!tooltip) return;

    const rarityColor = RARITY_COLORS[itemDef.rarity];

    tooltip.innerHTML = `
      <div style="color: ${rarityColor}; font-weight: bold; margin-bottom: 5px;">
        ${itemDef.icon} ${itemDef.name}
      </div>
      <div style="color: #888; font-size: 11px; margin-bottom: 5px;">
        ${itemDef.type.charAt(0).toUpperCase() + itemDef.type.slice(1)}
      </div>
      <div style="margin-bottom: 5px;">${itemDef.description}</div>
      ${itemDef.stats ? this.formatStats(itemDef.stats) : ''}
      <div style="color: #ffd700; margin-top: 5px;">Sell: ${itemDef.sellPrice}g</div>
      ${item.quantity > 1 ? `<div style="color: #aaa;">Quantity: ${item.quantity}</div>` : ''}
    `;

    tooltip.style.display = 'block';
    tooltip.style.left = `${event.clientX + 15}px`;
    tooltip.style.top = `${event.clientY + 15}px`;
  }

  private formatStats(stats: Record<string, number | undefined>): string {
    const lines: string[] = [];
    if (stats.damage) lines.push(`<div style="color: #ff6666;">+${stats.damage} Damage</div>`);
    if (stats.armor) lines.push(`<div style="color: #66ff66;">+${stats.armor} Armor</div>`);
    if (stats.health) lines.push(`<div style="color: #66ff66;">+${stats.health} Health</div>`);
    if (stats.mana) lines.push(`<div style="color: #6666ff;">+${stats.mana} Mana</div>`);
    if (stats.strength) lines.push(`<div style="color: #ff9966;">+${stats.strength} Strength</div>`);
    if (stats.agility) lines.push(`<div style="color: #99ff66;">+${stats.agility} Agility</div>`);
    if (stats.intelligence) lines.push(`<div style="color: #9999ff;">+${stats.intelligence} Intelligence</div>`);
    return lines.join('');
  }

  private hideTooltip(): void {
    const tooltip = document.getElementById('inventory-tooltip');
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  }

  public addItem(itemId: string, quantity: number = 1): boolean {
    const itemDef = ITEMS[itemId];
    if (!itemDef) {
      console.warn(`Unknown item: ${itemId}`);
      return false;
    }

    // If stackable, try to add to existing stack
    if (itemDef.stackable) {
      for (let i = 0; i < this.items.length; i++) {
        const slot = this.items[i];
        if (slot && slot.itemId === itemId && slot.quantity < itemDef.maxStack) {
          const canAdd = Math.min(quantity, itemDef.maxStack - slot.quantity);
          slot.quantity += canAdd;
          quantity -= canAdd;
          this.updateSlotUI(i);

          if (quantity <= 0) {
            this.onItemPickup?.(itemDef, canAdd);
            return true;
          }
        }
      }
    }

    // Find empty slot for remaining
    while (quantity > 0) {
      const emptySlot = this.items.findIndex((s) => s === null);
      if (emptySlot === -1) {
        console.log('Inventory full!');
        return false;
      }

      const addQuantity = itemDef.stackable ? Math.min(quantity, itemDef.maxStack) : 1;
      this.items[emptySlot] = {
        itemId,
        quantity: addQuantity,
        slot: emptySlot,
      };
      quantity -= addQuantity;
      this.updateSlotUI(emptySlot);
    }

    this.onItemPickup?.(itemDef, quantity);
    return true;
  }

  public removeItem(itemId: string, quantity: number = 1): boolean {
    for (let i = 0; i < this.items.length; i++) {
      const slot = this.items[i];
      if (slot && slot.itemId === itemId) {
        if (slot.quantity >= quantity) {
          slot.quantity -= quantity;
          if (slot.quantity <= 0) {
            this.items[i] = null;
          }
          this.updateSlotUI(i);
          return true;
        }
      }
    }
    return false;
  }

  public hasItem(itemId: string, quantity: number = 1): boolean {
    let total = 0;
    for (const slot of this.items) {
      if (slot && slot.itemId === itemId) {
        total += slot.quantity;
        if (total >= quantity) return true;
      }
    }
    return false;
  }

  public getItemCount(itemId: string): number {
    let total = 0;
    for (const slot of this.items) {
      if (slot && slot.itemId === itemId) {
        total += slot.quantity;
      }
    }
    return total;
  }

  public addGold(amount: number): void {
    this.gold += amount;
    this.updateGoldUI();
    this.onGoldChange?.(this.gold);
  }

  public removeGold(amount: number): boolean {
    if (this.gold >= amount) {
      this.gold -= amount;
      this.updateGoldUI();
      this.onGoldChange?.(this.gold);
      return true;
    }
    return false;
  }

  public getGold(): number {
    return this.gold;
  }

  private updateSlotUI(slotIndex: number): void {
    const slotEl = document.querySelector(`[data-slot="${slotIndex}"]`) as HTMLDivElement;
    if (!slotEl) return;

    const item = this.items[slotIndex];
    if (!item) {
      slotEl.innerHTML = '';
      slotEl.style.borderColor = '#3a3a5a';
      return;
    }

    const itemDef = ITEMS[item.itemId];
    if (!itemDef) return;

    const rarityColor = RARITY_COLORS[itemDef.rarity];
    slotEl.style.borderColor = rarityColor;
    slotEl.innerHTML = `
      <span style="font-size: 24px;">${itemDef.icon}</span>
      ${item.quantity > 1 ? `<span style="position: absolute; bottom: 2px; right: 4px; font-size: 11px; color: #fff; text-shadow: 1px 1px 2px #000;">${item.quantity}</span>` : ''}
    `;
  }

  private updateGoldUI(): void {
    const goldEl = document.getElementById('inventory-gold');
    if (goldEl) {
      goldEl.textContent = `💰 ${this.gold}`;
    }
  }

  public open(): void {
    this.isOpen = true;
    this.container.style.display = 'block';
    // Update all slots
    for (let i = 0; i < this.maxSlots; i++) {
      this.updateSlotUI(i);
    }
    this.updateGoldUI();
  }

  public close(): void {
    this.isOpen = false;
    this.container.style.display = 'none';
    this.hideTooltip();
  }

  public toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  public isInventoryOpen(): boolean {
    return this.isOpen;
  }

  public getAllItems(): (InventoryItem | null)[] {
    return this.items;
  }

  public getItem(slotIndex: number): InventoryItem | null {
    return this.items[slotIndex] ?? null;
  }

  public destroy(): void {
    this.container.remove();
    document.getElementById('inventory-tooltip')?.remove();
  }
}
